// Restricted (c) Siemens Healthineers AG, 2025, All rights reserved.

import { CommonModule } from '@angular/common';
import {
  CdkConnectedOverlay,
  Overlay,
  OverlayModule,
} from '@angular/cdk/overlay';
import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { CtsGantryInitEvent } from '../../interfaces/cts-gantry-init-event';
import { CtsGantryLog } from '../../interfaces/cts-gantry-log';
import { CtsGantryFilterLogService } from '../../services/cts-gantry-filter-log.service';
import { CtstatusService } from '../../services/ctstatus.service';
import { SystemDetailsModel } from 'src/app/api/remote-api/.portal.generated/models';
import { APICallGateComponent } from 'src/app/modules/shared/components/api-call-gate.component';
import { Subject, takeUntil } from 'rxjs';
import { CtsGantryLogData } from '../../interfaces/cts-gantry-log-data';
import { CtsGantryScrollPaginationModel } from '../../model/cts-gantry-scroll-pagination-model';
import { CtsGantryLogEmptyStateComponent } from '../cts-gantry-log-empty-state/cts-gantry-log-empty-state.component';
import { CtsGantryCacheModel } from '../../model/cts-gantry-cache-model';
import { CtsGantryTimezonePipe } from '../../pipe/cts-gantry-timezone.pipe';
import { CtsGantryLogSeverityType } from '../../enum/cts-gantry-log-severity-type';
import { CtsGantryFilterDropdownComponent } from '../../cts-gantry-filter-dropdown/cts-gantry-filter-dropdown.component';

@Component({
  standalone: true,
  imports: [
    CommonModule,
    OverlayModule,
    TranslateModule,
    RouterLink,
    APICallGateComponent,
    CtsGantryTimezonePipe,
    CtsGantryLogEmptyStateComponent,
    CtsGantryFilterDropdownComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  selector: 'app-cts-gantry-log-table',
  templateUrl: './cts-gantry-log-table.component.html',
  styleUrl: './cts-gantry-log-table.component.scss',
})
export class CtsGantryLogTableComponent implements OnDestroy, OnChanges {
  @Input({ required: true }) ctsEvent!: CtsGantryInitEvent;
  @Input({ required: true }) systemDetails!: SystemDetailsModel;
  @Input() logCacheModel = new CtsGantryCacheModel<CtsGantryLogData>();
  @Output() cancelChange = new EventEmitter<void>();

  @ViewChild('searchInput') searchInput: ElementRef | undefined;
  @ViewChild(CdkConnectedOverlay) cdkConnectedOverlay:
    | CdkConnectedOverlay
    | undefined;

  logs: CtsGantryLog[] = [];
  filteredLogs = this.logs;
  protected readonly STATUS_ERROR = CtsGantryLogSeverityType.ERROR;
  protected eventLogs = this.ctsystemService.gantryEventLogs;
  protected isfilterDropdownHeaderOpen = false;
  protected readonly pagination = new CtsGantryScrollPaginationModel();
  protected scrollStrategy = this.overlay.scrollStrategies.close();

  private readonly unsubscribe = new Subject<void>();

  public constructor(
    protected readonly filterLogService: CtsGantryFilterLogService,
    protected readonly ctsystemService: CtstatusService,
    private readonly overlay: Overlay,
    private readonly renderer: Renderer2,
  ) {
    this.filterLogService.initLogFilterService(
      this.logs,
      this.updateFilteredLogs,
    );
    this.listenToLogs();
  }

  public ngOnChanges(): void {
    if (this.ctsEvent && this.systemDetails) {
      this.clearSearchInput();
      this.requestEventLogs();
    }
  }

  public ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
    this.filterLogService.unsubscribeSubjects();
  }

  private clearSearchInput() {
    if (this.searchInput) {
      this.renderer.setProperty(this.searchInput.nativeElement, 'value', '');
    }
  }

  private requestEventLogs() {
    if (!this.logCacheModel.hasData(this.ctsEvent.eventSource)) {
      this.eventLogs.request({
        MaterialNumber: this.systemDetails.systemMaterialNumber,
        SerialNumber: this.systemDetails.systemSerialNumber,
        EventSource: this.ctsEvent.eventSource,
      });
    } else {
      this.getFromCache();
    }
  }

  private getFromCache(): void {
    if (this.ctsEvent) {
      const logData = this.logCacheModel.getCachedData(
        this.ctsEvent.eventSource,
      );
      if (logData) {
        this.initLogAndFilter(logData);
      }
    }
  }

  private listenToLogs(): void {
    this.eventLogs.lastSuccessfulData$
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(this.logsSubscribeFn);
  }

  private readonly logsSubscribeFn = (res: any) => {
    if (this.ctsEvent) {
      this.logCacheModel.cacheData(this.ctsEvent.eventSource, res);
      this.initLogAndFilter(res);
    }
  };

  private initLogAndFilter(res: CtsGantryLogData): void {
    if (res?.logs) {
      this.logs = res.logs;
    } else {
      this.logs = [];
    }
    this.filterLogService.initLogFilterService(
      this.logs,
      this.updateFilteredLogs,
    );
  }

  private readonly updateFilteredLogs = (logs: CtsGantryLog[]) => {
    this.pagination.totalElements = logs.length;
    this.pagination.pageIndex = 0;
    this.pagination.updatePageFn = (start: number, end: number) => {
      this.filteredLogs.push(...logs.slice(start, end));
    };

    this.filteredLogs = logs.slice(0, this.pagination.pageSize);
  };

  protected onCancelClick(): void {
    this.cancelChange.emit();
  }
}
